import { ShopOutlined } from '@ant-design/icons';
import {
  Empty,
  Input,
  Radio,
  Spin,
  Table,
  Tag,
  type RadioChangeEvent,
  type TableProps,
} from 'antd';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { PageTitle } from '@client/components/PageTitle';
import { EAbilitySubjects } from '@client/hooks/useAbilities';
import { PageLayout } from '@client/layouts/page.layout';
import { FeatureToggle } from '@client/pages/admin/marketplace/feature-toggle';
import { useMarketplaceQueries } from '@client/queries/useMarketplaceQueries';

import styles from './marketplace.module.less';

import type { IMarketplaceListItem } from '@common/types';
import type { ColumnsType } from 'antd/es/table';

type TFeaturedFilter = 'all' | 'featured';

const PAGE_SIZE = 50;

/**
 * Admin marketplace curation page. Lists public user subscriptions with
 * a name search, featured filter, and a per-row featured switch (admin-only).
 * @returns {React.JSX.Element} The rendered page
 */
export const AdminMarketplacePage: React.FC = (): React.JSX.Element => {
  const { t } = useTranslation();
  const { useMarketplaceList } = useMarketplaceQueries();

  const [searchInput, setSearchInput] = useState<string>('');
  const [search, setSearch] = useState<string>('');
  const [filter, setFilter] = useState<TFeaturedFilter>('all');

  const queryParams = useMemo(
    () => ({
      limit: PAGE_SIZE,
      ...(filter === 'featured' ? { featured: true } : {}),
      ...(search ? { q: search } : {}),
    }),
    [filter, search],
  );

  const { data, isLoading, isFetching } = useMarketplaceList(queryParams);

  const items: IMarketplaceListItem[] = data?.items ?? [];

  const onSearchChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    setSearchInput(event.target.value);
  };

  const onSearchSubmit = (value: string): void => {
    setSearch(value.trim());
  };

  const onFilterChange = (event: RadioChangeEvent): void => {
    setFilter(event.target.value as TFeaturedFilter);
  };

  const columns: ColumnsType<IMarketplaceListItem> = [
    {
      title: t('admin.marketplace.column.name'),
      dataIndex: 'name',
      key: 'name',
      ellipsis: true,
    },
    {
      title: t('admin.marketplace.column.owner'),
      dataIndex: 'ownerHandle',
      key: 'ownerHandle',
      render: (handle: string) => (handle ? `@${handle}` : '-'),
    },
    {
      title: t('admin.marketplace.column.total'),
      dataIndex: 'totalPrice',
      key: 'totalPrice',
      align: 'right',
      render: (totalPrice: number, record: IMarketplaceListItem) =>
        `${(totalPrice ?? 0).toFixed(2)} ${record.currency ?? 'USD'}`,
    },
    {
      title: t('admin.marketplace.column.features'),
      dataIndex: 'featureCount',
      key: 'featureCount',
      align: 'right',
    },
    {
      title: t('admin.marketplace.column.clones'),
      dataIndex: 'cloneCount',
      key: 'cloneCount',
      align: 'right',
    },
    {
      title: t('admin.marketplace.column.public'),
      key: 'public',
      align: 'center',
      // The marketplace listing only returns `public: true` items.
      render: () => <Tag color="green">{t('admin.marketplace.publicYes')}</Tag>,
    },
    {
      title: t('admin.marketplace.column.featured'),
      key: 'featured',
      align: 'center',
      render: (_, record: IMarketplaceListItem) => (
        <FeatureToggle id={record.id} featured={record.featured} />
      ),
    },
  ];

  const tableProps: TableProps<IMarketplaceListItem> = {
    columns,
    dataSource: items,
    rowKey: (record) => record.id,
    loading: isLoading || isFetching,
    pagination: {
      pageSize: PAGE_SIZE,
      showSizeChanger: false,
    },
    bordered: true,
    size: 'small',
    locale: {
      emptyText: (
        <div className={styles.empty}>
          <Empty
            description={t('admin.marketplace.empty')}
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          />
        </div>
      ),
    },
    title: () => (
      <div className={styles.filters}>
        <Input.Search
          allowClear
          placeholder={t('admin.marketplace.search')}
          value={searchInput}
          onChange={onSearchChange}
          onSearch={onSearchSubmit}
          style={{ maxWidth: 320 }}
        />
        <Radio.Group value={filter} onChange={onFilterChange}>
          <Radio.Button value="all">
            {t('admin.marketplace.filters.all')}
          </Radio.Button>
          <Radio.Button value="featured">
            {t('admin.marketplace.filters.featured')}
          </Radio.Button>
        </Radio.Group>
        <span className={styles.spacer} />
      </div>
    ),
  };

  return (
    <PageLayout
      subject={EAbilitySubjects.MARKETPLACE}
      title={
        <PageTitle Icon={ShopOutlined} name={t('admin.marketplace.title')} />
      }
    >
      {isLoading && !data ? (
        <Spin spinning />
      ) : (
        <Table<IMarketplaceListItem> {...tableProps} />
      )}
    </PageLayout>
  );
};

export default AdminMarketplacePage;
